import { Serializer } from './../../../helpers/serializer/seriealizer';
import { CurrenciesModelResponse } from './../response/models/currencies.model';
import { CurrenciesInterfaceResponse } from './../response/interfaces/currencies.interface';
import { Inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { catchError, map, takeUntil } from 'rxjs/operators';
import {
  BehaviorSubject,
  Subject,
  throwError,
  Observable,
  Observer,
} from 'rxjs';
import {
  TechLibPortalConfig,
  TECH_LIB_PORTAL_CONFIG,
} from '../../../tech-lib-portal.interceptor';
import { PublicPlannerEnum } from '../../../services/api-names';
import { Utils } from '../../../utils/utils';
import { SessionService } from '../../../services/session.service';
import { PortfolioInterface } from '../../profileportfolio/request/interfaces/portfolio.interface';
import { SimulationObjInterface } from '../../financialplanning/request/interfaces/simulation-obj.interface';
import { FinancialPlanRefdataInterface } from '../../financialplanning/request/interfaces/financialplan-refdata.interface';
import { EvalGoalInterface } from '../../financialplanning/request/interfaces/eval-goal.interface';
import { FeeInterface } from '../../financialplanning/request/interfaces/fee.interface';
import { ModelPortfolioInitContributionInterface } from '..';

@Injectable({
  providedIn: 'root',
})
export class InitcontributionService {
  private unsubscribe$ = new Subject<void>();
  public profileAnalysisSource = new BehaviorSubject<PortfolioInterface>(
    {} as any
  );
  public profileAnalysis$ = this.profileAnalysisSource.asObservable();
  public simulationObjSource = new BehaviorSubject<SimulationObjInterface>(
    {} as any
  );
  public simulationObj$ = this.simulationObjSource.asObservable();
  public fpReferenceDataSource =
    new BehaviorSubject<FinancialPlanRefdataInterface>({} as any);
  public fpReferenceData$ = this.fpReferenceDataSource.asObservable();
  public evalGoalObjSource = new BehaviorSubject<EvalGoalInterface>({} as any);
  public evalGoalObj$ = this.evalGoalObjSource.asObservable();
  public agreeRiskWarningSource = new BehaviorSubject<boolean>(false);
  public agreeRiskWarning$ = this.agreeRiskWarningSource.asObservable();
  public errorMinimunSource = new BehaviorSubject<any>({} as any);
  public errorMinimun$ = this.errorMinimunSource.asObservable();

  constructor(
    @Inject(TECH_LIB_PORTAL_CONFIG) private config: TechLibPortalConfig,
    private session: SessionService,
    private httpBackend: HttpBackend,
    private http: HttpClient,
    private serializer: Serializer
  ) {}

  public getEvalGoal(obj: SimulationObjInterface) {
    var url = this.config.baseApi + PublicPlannerEnum.apirest_eval;

    let bodyRequest;
    bodyRequest = {
      Currency: obj.Currency,
      ProfileId: obj.ProfileId,
      GoalAmount: obj.GoalAmount,
      TimeHorizon: obj.TimeHorizon,
      InitialContributionAmount: obj.InitialContributionAmount,
      PeriodicContributionAmount: obj.PeriodicContributionAmount,
      PeriodicContributionFrequency: obj.PeriodicContributionFrequency,
      Priority: obj.Priority,
      InitialDate: obj.InitialDate,
      Type: 'SAVING',
      Name: 'Increase my wealth',
    };
    bodyRequest = JSON.stringify(bodyRequest);
    return this.http.post<EvalGoalInterface>(url, bodyRequest).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getProfileAnalysis(goalAmount: number, profileId: string) {
    const amount = goalAmount == null ? 0 : goalAmount;
    let url = Utils.formatString(
      this.config.baseApi + PublicPlannerEnum.apirest_profile_analysis,
      ['SAVING', profileId, amount.toString()]
    );

    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getFinancialPlanRefData() {
    var url = this.config.baseApi + PublicPlannerEnum.apirest_eval_refdata;

    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getFees(profileId: string) {
    let url = Utils.formatString(
      this.config.baseApi + PublicPlannerEnum.apirest_fees,
      ['SAVING', profileId]
    );

    return this.http.get<FeeInterface>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public saveGoal(goal: SimulationObjInterface) {
    let url = this.config.baseApi + PublicPlannerEnum.apirest_goal;
    let bodyRequest = JSON.stringify(goal);

    return this.http.post<any>(url, bodyRequest).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public saveGoalPortfolio(goalId: string, portfolioCode: string) {
    let url = Utils.formatString(
      this.config.baseApi + PublicPlannerEnum.apirest_goal_portfolio,
      [goalId]
    );

    //let bodyRequest = { PortfolioCodes: [portfolioCode] };
    let bodyRequest = [portfolioCode];

    return this.http.post<any>(url, bodyRequest).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public funding(bodyRequest: any, portfolioCode: string) {
    let url = Utils.formatString(
      this.config.baseApi + PublicPlannerEnum.apirest_funding,
      [portfolioCode]
    );

    return this.http.post<any>(url, bodyRequest).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  getCurrencies(): Observable<CurrenciesInterfaceResponse> {
    let url = this.config.baseApi + PublicPlannerEnum.apirest_currencies;

    return new Observable((observer: Observer<CurrenciesInterfaceResponse>) => {
      this.http
        .get(url)
        .pipe(
          map((resp: any) => {
            // console.log(resp);
            const t = this.serializer.fromJson(resp, CurrenciesModelResponse);
            return t;
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  public getModelPortfolio(modelPortfolioCode: string) {
    let url = Utils.formatString(
      this.config.baseApi + PublicPlannerEnum.apirest_modelportfolio,
      [modelPortfolioCode]
    );
    return this.http.get<ModelPortfolioInitContributionInterface>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getModelPortfoliosServicemodel(
    modelPortfolio: string,
    profileid: string
  ) {
    let url = Utils.formatString(
      this.config.baseApi +
        PublicPlannerEnum.apirest_modelportfolio_servicemodel,
      [modelPortfolio, profileid]
    );
    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }
}
